import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmationModal } from './modals';
import ChangePasswordCard from './features/user/account/ChangePasswordCard';
import useAuth from '../hooks/useAuth';
import useAppState from '../hooks/useAppState';
import { MCE_EVENTS_QUERY_KEY } from '../hooks/api/useMceEvents';
import { RECENT_MCE_EVENTS_QUERY_KEY } from '../hooks/api/useRecentMceEvents';
import { openConfirm, closeConfirm } from '../redux/slices/app';
import { addNotification } from '../redux/slices/notifications';
import { setService } from '../redux/slices/services';
import { getCounters } from '../redux/slices/dashboard';
import { useDispatch, useSelector } from '../redux/store';
import { PATH_PAGE } from '../routes/paths';

// ------------------------------------------------------------------------------

const SocketEvents = () => {
  const { isInitialized, on, reconnect, user } = useAuth();
  const { isAppActive, isAppActiveRef, isOnlineRef } = useAppState();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { confirm } = useSelector((state) => state.app);
  const { month } = useSelector((state) => state.dashboard);

  // Garde `month` accessible aux listeners socket sans déclencher leur
  // ré-attache à chaque changement de mois (Socket.on() fait removeAllListeners
  // avant chaque attache — cf. handler `validatePdsFds` ci-dessous).
  const monthRef = useRef(month);
  useEffect(() => {
    monthRef.current = month;
  }, [month]);

  const pendingEventRef = useRef(null);

  // ----------------------------------------------------------------------------

  useEffect(() => {
    if (isInitialized) {
      // New notification received
      on('notification', (notification) => {
        dispatch(addNotification(notification));
      });

      // A shift clocking in or out has been made.
      // IMPORTANT : ne pas re-enregistrer ce listener ailleurs (ex: dans
      // une page de timeline). Socket.on() fait removeAllListeners avant
      // chaque attache → ça détruirait silencieusement ce handler global
      // et figerait la mise à jour temps réel des services/compteurs.
      on('validatePdsFds', (shift) => {
        dispatch(setService(shift));
        dispatch(getCounters({ month: monthRef.current, onlyAnomalies: true }));
        // Refetch transparent de la timeline MCE si l'utilisateur la regarde
        // (TanStack ignore l'invalidation si aucune query ne matche).
        queryClient.invalidateQueries({ queryKey: MCE_EVENTS_QUERY_KEY });
      });

      // Déclaration d'un évènement MCE par un autre utilisateur de la société.
      // Rafraîchit event list, dashboard manager et widget activités récentes.
      on('mceEventDeclared', () => {
        queryClient.invalidateQueries({ queryKey: MCE_EVENTS_QUERY_KEY });
        queryClient.invalidateQueries({ queryKey: ['form-entries'] });
        queryClient.invalidateQueries({ queryKey: RECENT_MCE_EVENTS_QUERY_KEY });
      });

      // Socket reconnected after a transport failure
      on('reconnected', (attempt) => {
        console.debug({ reconnected: attempt });
        pendingEventRef.current = null;
        if (!window.env.isClockingApp && window.location.pathname.includes('/maintenance')) {
          navigate('/');
        }
      });

      // Socket disconnected (transport failure — fired after 3 reconnect attempts)
      on('disconnected', (reason) => {
        console.debug({ disconnected: reason });

        if (!isAppActiveRef.current) {
          pendingEventRef.current = { type: 'disconnected', reason };
          return;
        }

        // No network: the offline banner provides feedback, don't navigate.
        // MaintenancePage is lazy-loaded and would fail to fetch anyway.
        if (!isOnlineRef.current) return;

        // Foreground + online + server unreachable: show maintenance page.
        // Keeps the "Changer de société" escape hatch for multi-tenant users.
        navigate(PATH_PAGE.maintenance);
      });
    }
  }, [isInitialized, dispatch, navigate, on, queryClient, isAppActiveRef, isOnlineRef]);

  // ----------------------------------------------------------------------------
  // Foreground return — process pending events and trigger reconnection
  // ----------------------------------------------------------------------------

  useEffect(() => {
    if (!isAppActive || !isInitialized) return;

    pendingEventRef.current = null;

    // Always attempt reconnection on foreground return (no-op if already connected)
    reconnect();
  }, [isAppActive, isInitialized, reconnect]);

  // Temporary Comète password
  if (user && (user.isFirstLogin || user.isPwdExpired) && !confirm.open) {
    dispatch(
      openConfirm({
        variant: 'info',
        title: 'Votre mot de passe a expiré, veuillez le modifier.',
        children: <ChangePasswordCard hideCard isNewOrExpired onLogOut={() => dispatch(closeConfirm())} />
      })
    );
  }

  return <ConfirmationModal {...confirm}>{confirm.children}</ConfirmationModal>;
};

export default SocketEvents;
